import { colors } from '@/constants/colors'
import { Pressable } from '@/ui/core/pressable'
import { HStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { cn } from '@/ui/utils'
import { View } from 'react-native'

/** Nought to ten, the industry's scale, so a score here is comparable with one collected anywhere. */
const STEPS = Array.from({ length: 11 }, (_, index) => index)

/**
 * The scale, drawn as a scale.
 *
 * Eleven cells in one row rather than a list of eleven options, because what is being answered
 * is a position between two ends and not a choice between eleven things: the row itself says
 * "nought over here, ten over there", which no stack of rows can. Each cell takes an equal
 * share of the line, so the scale is the width of the note whatever the phone.
 *
 * The chosen one fills black, as every selected chip in this app does, and the ends are named
 * underneath — the numbers alone do not say which way is good.
 */
export const NpsScale = ({
	value,
	onChange,
}: { value: number | null; onChange: (score: number) => void }) => (
	<View>
		<HStack gap={3}>
			{STEPS.map((score) => {
				const isChosen = score === value
				return (
					<Pressable
						key={score}
						onPress={() => onChange(score)}
						accessibilityRole="radio"
						accessibilityState={{ checked: isChosen }}
						accessibilityLabel={`${score} out of 10`}
						// Eleven cells across a phone are twenty-seven points wide, so the height is
						// held near that to keep a cell a cell rather than a tall thin pill. The tap
						// target is given back vertically, where there is nothing to collide with —
						// the cells are already touching side to side.
						hitSlop={{ top: 9, bottom: 9 }}
						className={cn(
							'h-9 flex-1 items-center justify-center rounded-brand',
							isChosen ? 'bg-ink' : 'bg-white'
						)}
						style={isChosen ? undefined : { borderWidth: 1, borderColor: colors.edge }}
					>
						{/* The numeral in the brand's own face, as this app sets every number. */}
						<Text
							className="font-display text-[15px] leading-[18px] tracking-[-0.02em]"
							tone={isChosen ? 'white' : 'ink'}
						>
							{score}
						</Text>
					</Pressable>
				)
			})}
		</HStack>
		<HStack className="mt-2.5 justify-between">
			<Text size="body3" tone="soft" className="tracking-[0.08em]">
				Not likely
			</Text>
			<Text size="body3" tone="soft" className="tracking-[0.08em]">
				Very likely
			</Text>
		</HStack>
	</View>
)
